import { useMemo, useState } from 'react';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';
import DatePicker from '../ui/DatePicker';
import CandidateFilterBar, { useCandidateFilters } from '../filters/CandidateFilterBar';

export const STATUS_TONE = { pending: 'amber', approved: 'green', rejected: 'red' };

export function StatusBadge({ status }) {
  return (
    <Badge tone={STATUS_TONE[status] || 'gray'} className="capitalize">
      {status}
    </Badge>
  );
}

const EMPTY = { status: 'pending', search: '', from: '', to: '' };

/**
 * Filter state for the request list pages. `status` is sent to the API (so the server only
 * returns that status); search and the request-date range are applied to the returned rows.
 * `getFields(row)` returns the strings the search box matches against; `getRequestedAt(row)` the
 * request timestamp the date range applies to.
 */
export function useRequestFilters(getFields, getRequestedAt) {
  const [filters, setFilters] = useState(EMPTY);
  // Vehicle / location / license / workflow / step: sent to the API, which filters the requests.
  const candidate = useCandidateFilters();

  const apply = useMemo(() => {
    const term = filters.search.trim().toLowerCase();
    // Compare on local calendar days so "to" includes the whole end day.
    const from = filters.from ? new Date(`${filters.from}T00:00:00`) : null;
    const to = filters.to ? new Date(`${filters.to}T23:59:59.999`) : null;

    return (rows) =>
      rows.filter((row) => {
        if (term && !getFields(row).some((v) => String(v ?? '').toLowerCase().includes(term))) return false;
        const at = new Date(getRequestedAt(row));
        if (from && at < from) return false;
        if (to && at > to) return false;
        return true;
      });
    // getFields/getRequestedAt are stable module-level functions in the pages.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search, filters.from, filters.to]);

  const set = (key) => (e) => setFilters((prev) => ({ ...prev, [key]: e.target.value }));
  const dirty = filters.search || filters.from || filters.to || filters.status !== EMPTY.status || candidate.active;

  return {
    filters,
    set,
    apply,
    candidate,
    reset: () => {
      setFilters(EMPTY);
      candidate.reset();
    },
    dirty,
  };
}

export default function RequestFilters({ filters, set, reset, dirty, total, shown, candidate, hideStatus = false }) {
  return (
    <div className="mb-4 rounded-lg border border-gray-200 bg-white p-4">
      {candidate && <CandidateFilterBar {...candidate.barProps} active={false} className="mb-3" />}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Input
          label="Search"
          placeholder="Name, ref no, contact, nationality…"
          value={filters.search}
          onChange={set('search')}
          className="lg:col-span-2"
        />
        {!hideStatus && (<Select label="Status" value={filters.status} onChange={set('status')}>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="all">All</option>
        </Select>)}
        <DatePicker label="Requested from" value={filters.from} max={filters.to || undefined} onChange={set('from')} />
        <DatePicker label="Requested to" value={filters.to} min={filters.from || undefined} onChange={set('to')} />
      </div>
      <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
        <span>
          Showing {shown} of {total}
        </span>
        {dirty && (
          <Button variant="secondary" className="px-3 py-1 text-xs" onClick={reset}>
            Clear filters
          </Button>
        )}
      </div>
    </div>
  );
}
